import type i18n from "i18next"

export interface I18nDiagnostics {
  initialized: boolean
  language: string | null
  resolvedLanguage: string | null
  namespaces: string[]
  loadedNamespaces: string[]
  defaultNamespace: string | readonly string[] | null
}

declare global {
  interface Window {
    __celestiaI18nDiagnostics?: () => I18nDiagnostics
  }
}

const collectConfiguredNamespaces = (instance: typeof i18n): string[] => {
  const configured: unknown = instance.options.ns

  if (Array.isArray(configured)) {
    return configured.filter((ns): ns is string => typeof ns === "string")
  }

  if (typeof configured === "string") {
    return [configured]
  }

  return []
}

export const installI18nDiagnostics = (instance: typeof i18n): void => {
  if (typeof window === "undefined") return

  window.__celestiaI18nDiagnostics = () => {
    const namespaces = collectConfiguredNamespaces(instance)
    const defaultNamespace = instance.options.defaultNS

    return {
      initialized: instance.isInitialized,
      language: instance.language || null,
      resolvedLanguage: instance.resolvedLanguage || null,
      namespaces,
      loadedNamespaces: namespaces.filter(ns => instance.hasLoadedNamespace(ns)),
      defaultNamespace: defaultNamespace === false || defaultNamespace === undefined ? null : defaultNamespace
    }
  }
}

export const installI18nDevLogging = (instance: typeof i18n): void => {
  instance.on("missingKey", (lngs, ns, key) => {
    if (import.meta.env.DEV) {
      console.warn(`[i18n] Missing key: ${ns}:${key} for languages: ${lngs.join(", ")}`)
    }
  })

  instance.on("failedLoading", (lng, ns, msg) => {
    console.error(`[i18n] Failed to load translations: ${lng}/${ns}:`, msg)
  })
}

export const installI18nDocumentLanguage = (instance: typeof i18n): void => {
  const updateDocumentLanguage = (language: string): void => {
    if (typeof document === "undefined") return

    document.documentElement.lang = language
    document.documentElement.dir = language === "ar" || language === "fa" ? "rtl" : "ltr"
  }

  instance.on("languageChanged", updateDocumentLanguage)
  instance.on("initialized", () => {
    if (instance.language) updateDocumentLanguage(instance.language)
  })

  if (instance.language) updateDocumentLanguage(instance.language)
}
